import { useState, useEffect, useRef, useCallback } from 'react';

const GOLD = '#C9A84C';
const SAFETY_TIMEOUT_MS = 20000;

const STATUS_TEXT = {
  checking: 'Checking for updates\u2026',
  downloading: 'Downloading update\u2026',
  installing: 'Installing update\u2026',
  restarting: 'Restarting Maestro\u2026',
};

export default function UpdateTransition() {
  const [state, setState] = useState({ visible: false, status: 'checking', newVersion: '' });
  const [fading, setFading] = useState(false);
  const safetyTimer = useRef(null);
  const hasSetup = useRef(false);

  const dismiss = useCallback(() => {
    setFading(true);
    setTimeout(() => {
      setState({ visible: false, status: 'checking', newVersion: '' });
      setFading(false);
    }, 400);
  }, []);

  const resetSafetyTimer = useCallback(() => {
    if (safetyTimer.current) clearTimeout(safetyTimer.current);
    safetyTimer.current = setTimeout(dismiss, SAFETY_TIMEOUT_MS);
  }, [dismiss]);

  useEffect(() => {
    if (hasSetup.current) return;
    hasSetup.current = true;

    const tauri = window.__TAURI__;
    if (!tauri?.event?.listen) return;

    const listen = tauri.event.listen.bind(tauri.event);
    const unlisteners = [];

    const setup = async () => {
      unlisteners.push(await listen('maestro://update-checking', () => {
        setState({ visible: true, status: 'checking', newVersion: '' });
        resetSafetyTimer();
      }));

      unlisteners.push(await listen('maestro://update-downloading', (e) => {
        const version = e?.payload || '';
        setState(s => ({ ...s, visible: true, status: 'downloading', newVersion: version || s.newVersion }));
        resetSafetyTimer();
      }));

      unlisteners.push(await listen('maestro://update-installing', (e) => {
        const version = e?.payload || '';
        setState(s => ({ ...s, visible: true, status: 'installing', newVersion: version || s.newVersion }));
        resetSafetyTimer();
      }));

      unlisteners.push(await listen('maestro://update-restarting', (e) => {
        const version = e?.payload || '';
        setState(s => ({ ...s, visible: true, status: 'restarting', newVersion: version || s.newVersion }));
        if (safetyTimer.current) clearTimeout(safetyTimer.current);
      }));

      unlisteners.push(await listen('maestro://update-none', () => {
        dismiss();
      }));

      unlisteners.push(await listen('maestro://update-error', () => {
        dismiss();
      }));
    };

    setup();

    return () => {
      unlisteners.forEach(fn => fn());
      if (safetyTimer.current) clearTimeout(safetyTimer.current);
    };
  }, [dismiss, resetSafetyTimer]);

  if (!state.visible) return null;

  const currentVersion = window.__MAESTRO_CONNECTOR__?.version || '';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 999999,
      background: 'rgba(0, 0, 0, 0.85)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      opacity: fading ? 0 : 1,
      transition: 'opacity 0.4s ease',
    }}>
      <style>{`
        @keyframes updateFadeIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        @keyframes updateSpin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 32,
        padding: 48, maxWidth: 380, width: '100%',
        background: 'rgba(18, 18, 30, 0.8)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 24,
        boxShadow: '0 32px 80px rgba(0, 0, 0, 0.5)',
        animation: 'updateFadeIn 0.3s ease-out',
      }}>
        <span style={{
          fontSize: 20, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
          background: `linear-gradient(135deg, #fff 0%, ${GOLD} 60%, #E8D48B 100%)`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
        }}>
          Maestro
        </span>

        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          border: '3px solid rgba(255, 255, 255, 0.06)',
          borderTopColor: GOLD,
          animation: 'updateSpin 1.4s linear infinite',
        }} />

        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 17, fontWeight: 600, color: 'rgba(255,255,255,0.9)', margin: 0, lineHeight: 1.4 }}>
            {STATUS_TEXT[state.status] || 'Updating\u2026'}
          </p>
          {(currentVersion || state.newVersion) && (
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: '10px 0 0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
              {currentVersion ? `v${currentVersion}` : ''}
              {currentVersion && state.newVersion ? ' \u2192 ' : ''}
              {state.newVersion ? `v${state.newVersion}` : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
